import TrainingHistoryList from "issara-sdk/apis/TrainingHistoryList_apps_HRM";

import TrainingParticipantForHistorySerializer from "issara-sdk/types/TrainingParticipantForHistorySerializer_apps_HRM";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = { DEFAULT: "DEFAULT", SEARCH: "SEARCH" } as const;

export const SEQUENCE_NAME = "TrainingHistory" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  TrainingHistorySequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    filter: FilterType;
    pagination: {
      activePage: number;
      items: TrainingParticipantForHistorySerializer[];
      total: number;
    };
  }> | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

type FilterType = Partial<{
  course_end_date: string;
  course_id: number | string;
  course_start_date: string;
  course_status: string[];
  employee_id: number;
  training_type: string;
}>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
  userProfile?: Record<string, any>;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { activePage?: number };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  TrainingHistorySequence: null,
};

export const DataInitial: Data = {};

const Masters = [] as const;

const LIST_LIMIT = 100;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    {
      TrainingHistorySequence: {
        ...state.TrainingHistorySequence,
        sequenceIndex: "Action",
        filter: { employee_id: controller.data.userProfile?.employee },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const filter = state.TrainingHistorySequence?.filter;
  const activePage = params.activePage ?? 1;
  const offset = (activePage - 1) * LIST_LIMIT;

  const [result] = await TrainingHistoryList.list({
    apiToken: controller.apiToken,
    params: { limit: LIST_LIMIT, offset, ...filter },
  });

  controller.setState({
    TrainingHistorySequence: {
      ...state.TrainingHistorySequence,
      pagination: { activePage, items: result?.items || [], total: result?.total || 0 },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });
};
